import { useState, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import IntroBrandScreen from "./components/IntroBrandScreen";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ProductList from "./components/ProductList";
import Footer from "./components/Footer";
import GridModal from "./components/GridModal";
import CartModal from "./components/CartModal";
import OrdersModal from "./components/OrdersModal";
import ConfirmModal from "./components/ConfirmModal";
import AlertModal from "./components/AlertModal";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import "./styles.scss";

// Marca numérica (API/vitrine): 1 = Karsten, 2 = Trussardi, 3 = Karsten Decor
const MARCA_NUM_TO_BRAND_KEY = { 1: "karsten", 2: "trussardi", 3: "karsten_decor" };
const BRAND_KEY_TO_MARCA_NUM = { karsten: 1, trussardi: 2, karsten_decor: 3 };

function getBrandKey(nomeMarca) {
  if (!nomeMarca || typeof nomeMarca !== "string") return "outros";
  const normalized = nomeMarca.toLowerCase().trim().replace(/\s+/g, "_");
  return normalized || "outros";
}

function getVitrineKey(vitrine) {
  return String(vitrine?.IdVitrineVirtualB2b ?? "");
}

function resultToFamiliesAndRules(apiResult) {
  const vitrines = apiResult?.Vitrines;
  const produtosList = apiResult?.Produtos;
  if (!Array.isArray(vitrines) || !Array.isArray(produtosList)) {
    return { families: {}, businessRules: null, brandLabels: {}, brandKeys: [], vitrinesMeta: {} };
  }

  const produtosByRef = {};
  produtosList.forEach((p) => {
    if (p?.IdProdutoString != null) produtosByRef[p.IdProdutoString] = p;
  });

  const businessRules = {
    global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
    vitrines: {},
    categories: {},
  };

  const families = {};
  const brandLabels = {};
  const vitrinesMeta = {};

  const marcaNames = { 1: "KARSTEN", 2: "TRUSSARDI", 3: "KARSTEN DECOR" };

  vitrines.forEach((vitrine) => {
    const vitrineKey = getVitrineKey(vitrine);
    vitrinesMeta[vitrineKey] = {
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
    };
    businessRules.vitrines[vitrineKey] = {
      minValue: Number(vitrine.ValorMinimo) || 0,
      minQty: Number(vitrine.QuantidadeMinimaPedido) || 0,
      minInstallmentValue: Number(vitrine.DuplicataMinima) || 0,
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
    };

    const brandKey = vitrine.Marca != null ? (MARCA_NUM_TO_BRAND_KEY[vitrine.Marca] || "outros") : "outros";
    if (!brandLabels[brandKey]) brandLabels[brandKey] = marcaNames[vitrine.Marca] || brandKey.toUpperCase();

    const itens = vitrine.Itens || [];
    itens.forEach((item) => {
      const product = produtosByRef[item.IdProdutoString];
      if (!product) return;

      const familyKey = (product.Colecao || product.NomeComercial || "Geral").replace(/\s+/g, "_") || "Geral";
      if (!families[brandKey]) families[brandKey] = {};
      const byFamily = families[brandKey];
      if (!byFamily[familyKey]) {
        const fotosPorCor = product.FotosPorCor || [];
        byFamily[familyKey] = {
          name: product.Colecao || product.NomeComercial || "Produtos",
          commercialName: product.NomeComercial || product.Colecao || "",
          vitrine: vitrineKey,
          renderType: "card",
          status: product.Status || "A",
          category: product.Categoria || "",
          subcategory: product.Subcategoria || "",
          products: [],
          variants: fotosPorCor.length > 0 ? fotosPorCor.map((c) => c.NomeCor || "Geral") : ["Geral"],
          variantImages: {},
        };
        if (fotosPorCor.length > 0) {
          const urlsByVariant = {};
          fotosPorCor.forEach((c) => {
            const urls = c.Urls && Array.isArray(c.Urls) ? c.Urls : [];
            urlsByVariant[c.NomeCor || "Geral"] = urls;
          });
          byFamily[familyKey].variantImages = urlsByVariant;
        }
      }

      const productId = `${item.IdVitrineVirtualB2bItem}_${item.IdProdutoString}`;
      byFamily[familyKey].products.push({
        id: productId,
        idProduto: Number(product.IdProduto) || product.IdProdutoString,
        name: product.Nome || "",
        ref: product.IdProdutoString || "",
        price: Number(item.Valor) || 0,
        discount: Number(item.DescontoPercentual) || 0,
        multiple: 1,
        images: product.Fotos || [],
        especificacoes: product.Especificacoes || [],
        vitrine: vitrineKey,
        idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
        idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
        vitrineName: vitrine.Nome || "",
      });
    });
  });

  const brandKeys = Object.keys(families);
  brandKeys.forEach((k) => {
    if (!brandLabels[k]) brandLabels[k] = k.toUpperCase();
  });

  return { families, businessRules, brandLabels, brandKeys, vitrinesMeta };
}

const CART_STORAGE_KEY = "vitrine-empresa-cart";

function isTokenExpiredOrMissing() {
  try {
    const session = JSON.parse(localStorage.getItem("ApiCredentials") || "null");
    if (!session?.accessToken || !session?.dtExpiration) return true;
    const dtExp = new Date(session.dtExpiration).getTime();
    const dtNow = new Date().getTime();
    return dtNow >= dtExp;
  } catch {
    return true;
  }
}

const defaultBusinessRules = {
  global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
  vitrines: {},
  categories: {},
};

function getCartFromStorage(brandKeys = []) {
  try {
    if (!brandKeys || brandKeys.length === 0) return {};
    const raw = typeof window !== "undefined" ? localStorage.getItem(CART_STORAGE_KEY) : null;
    if (!raw) {
      const result = {};
      brandKeys.forEach((b) => { result[b] = {}; });
      return result;
    }
    const parsed = JSON.parse(raw);
    const result = {};
    brandKeys.forEach((b) => {
      result[b] = parsed[b] && typeof parsed[b] === "object" ? parsed[b] : {};
    });
    return result;
  } catch {
    const result = {};
    (brandKeys || []).forEach((b) => { result[b] = {}; });
    return result;
  }
}

function getCartForBrand(brand, brandKeys = []) {
  const all = getCartFromStorage(brandKeys);
  return all[brand] || {};
}

function saveCartForBrand(brand, cart, brandKeys = []) {
  try {
    const all = getCartFromStorage(brandKeys);
    all[brand] = cart && typeof cart === "object" ? cart : {};
    if (typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(all));
    }
  } catch (e) {
    console.error("Erro ao salvar carrinho no storage:", e);
  }
}

export default function VitrineEmpresaPage() {
  const [loading, setLoading] = useState(true);
  const [vitrineApiResult, setVitrineApiResult] = useState(null);

  useEffect(() => {
    if (isTokenExpiredOrMissing()) {
      localStorage.setItem("sessionExpired", "true");
      UtilsFncs.RedirectToLoginPage();
    }
  }, []);

  const [showIntroScreen, setShowIntroScreen] = useState(true);
  const [currentBrand, setCurrentBrand] = useState("");
  const theme =
    themeColorsDesingSystem.brands[
      currentBrand === "karsten_decor" ? "karsten" : currentBrand
    ] || themeColorsDesingSystem.brands.karsten;
  const [orderState, setOrderState] = useState({});
  const [isCartGrouped, setIsCartGrouped] = useState(false);
  const [activeFamilyKey, setActiveFamilyKey] = useState(null);
  const [tempGridState, setTempGridState] = useState({});
  const [selectedBulkColors, setSelectedBulkColors] = useState(new Set());
  const [selectedBulkProducts, setSelectedBulkProducts] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedSubcategories, setSelectedSubcategories] = useState(new Set());
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [selectedCommercialNames, setSelectedCommercialNames] = useState(
    new Set()
  );
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedVitrines, setSelectedVitrines] = useState(new Set());
  const [collectionSearchTerm, setCollectionSearchTerm] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState("");
  const [commercialSearchTerm, setCommercialSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [onlyStock, setOnlyStock] = useState(false);
  const [gridModalOpen, setGridModalOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [pedidosList, setPedidosList] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [submitOrderLoading, setSubmitOrderLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({ title: "", message: "", callback: null });
  const [alertType, setAlertType] = useState("success");

  const derived = useMemo(() => {
    if (!vitrineApiResult) return null;
    return resultToFamiliesAndRules(vitrineApiResult);
  }, [vitrineApiResult]);

  const effectiveFamilies = derived?.families ?? {};
  const effectiveBusinessRules = derived?.businessRules ?? defaultBusinessRules;
  const effectiveBrandKeys = derived?.brandKeys ?? [];
  const effectiveBrandLabels = derived?.brandLabels ?? {};
  const vitrinesMeta = derived?.vitrinesMeta ?? {};

  const primaryVitrine = useMemo(() => {
    const vitrines = vitrineApiResult?.Vitrines;
    if (!Array.isArray(vitrines) || vitrines.length === 0) return null;
    const comCliente = vitrines.find((v) => v.IdVitrineVirtualB2bCliente != null);
    return comCliente ?? vitrines[0];
  }, [vitrineApiResult]);

  const brandKeysWithProducts = useMemo(() => {
    const set = new Set();
    Object.entries(effectiveFamilies).forEach(([brandKey, familiesObj]) => {
      const total = Object.values(familiesObj || {}).reduce(
        (acc, fam) => acc + (fam.products?.length || 0),
        0
      );
      if (total > 0) set.add(brandKey);
    });
    return set;
  }, [effectiveFamilies]);

  useEffect(() => {
    async function fetchVitrines() {
      try {
        const response = await Api.get(
          "virtual-showcase-b2b-list-pdp",
          "showcaseB2B",
          "",
          true
        );

        if (!response) {
          setVitrineApiResult(null);
          return;
        }

        if (response.Success === false) {
          setVitrineApiResult(null);
          const msg = response.ErrorMessage || response.Message || response.ValidationMessage || "Não foi possível carregar as vitrines.";
          setAlertType("error");
          setAlertTitle("Atenção");
          setAlertMessage(msg);
          setAlertModalOpen(true);
          return;
        }

        const vitrines = response.Result?.Vitrines;
        const produtos = response.Result?.Produtos;
        if (vitrines && vitrines.length > 0) {
          setVitrineApiResult({ Vitrines: vitrines, Produtos: Array.isArray(produtos) ? produtos : [] });
        } else {
          setVitrineApiResult(null);
          setAlertType("error");
          setAlertTitle("Atenção");
          setAlertMessage("Você não possui vitrines disponíveis.");
          setAlertModalOpen(true);
        }
      } catch {
        setVitrineApiResult(null);
      } finally {
        setLoading(false);
      }
    }

    fetchVitrines();
  }, []);

  useEffect(() => {
    if (!vitrineApiResult || effectiveBrandKeys.length === 0) return;
    const all = getCartFromStorage(effectiveBrandKeys);
    const brandWithCart = effectiveBrandKeys.find((b) => Object.keys(all[b] || {}).length > 0);
    if (brandWithCart) {
      setCurrentBrand(brandWithCart);
      setOrderState(all[brandWithCart] || {});
      setShowIntroScreen(false);
    } else {
      setShowIntroScreen(true);
    }
  }, [vitrineApiResult, effectiveBrandKeys]);

  useEffect(() => {
    if (
      !showIntroScreen &&
      primaryVitrine &&
      effectiveBrandKeys.length > 0 &&
      !effectiveBrandKeys.includes(currentBrand)
    ) {
      const brandKeyFromMarca = primaryVitrine.Marca != null ? MARCA_NUM_TO_BRAND_KEY[primaryVitrine.Marca] : null;
      setCurrentBrand(brandKeyFromMarca && effectiveBrandKeys.includes(brandKeyFromMarca)
        ? brandKeyFromMarca
        : effectiveBrandKeys[0]);
    }
  }, [showIntroScreen, primaryVitrine, effectiveBrandKeys, currentBrand]);

  useEffect(() => {
    if (!ordersModalOpen || !primaryVitrine?.IdVitrineVirtualB2b) {
      if (!ordersModalOpen) return;
      setPedidosList([]);
      return;
    }
    const idVitrineVirtualB2b = primaryVitrine.IdVitrineVirtualB2b;
    const idMarca = currentBrand === 'karsten' ? 1 : currentBrand === 'trussardi' ? 2 : currentBrand === 'karsten_decor' ? 3 : null;
    let cancelled = false;
    setLoadingPedidos(true);
    async function fetchPedidos() {
      try {
        const params = new URLSearchParams({
          idVitrineVirtualB2b: idVitrineVirtualB2b,
        });
        if (idMarca !== "" && idMarca != null) params.set("idMarca", String(idMarca));
        const response = await Api.get(
          "virtual-showcase-b2b-list-pedidos",
          "showcaseB2B",
          `?${params.toString()}`,
          true
        );
        if (cancelled) return;
        if (response?.Success && response?.Result?.Pedidos) {
          setPedidosList(response.Result.Pedidos);
        } else {
          setPedidosList([]);
        }
      } catch {
        if (!cancelled) setPedidosList([]);
      } finally {
        if (!cancelled) setLoadingPedidos(false);
      }
    }
    fetchPedidos();
    return () => { cancelled = true; };
  }, [ordersModalOpen, primaryVitrine?.IdVitrineVirtualB2b, primaryVitrine?.Marca]);

  const fetchPedidoDetalhe = async (idVitrineVirtualB2bPedido) => {
    if (!primaryVitrine?.IdVitrineVirtualB2b) return null;
    try {
      const params = new URLSearchParams({
        idVitrineVirtualB2bPedido: idVitrineVirtualB2bPedido,
        idVitrineVirtualB2b: primaryVitrine.IdVitrineVirtualB2b,
      });
      const response = await Api.get(
        "virtual-showcase-b2b-pedido-detalhe",
        "showcaseB2B",
        `?${params.toString()}`,
        true
      );
      if (response?.Success && response?.Result) return response.Result;
      return null;
    } catch {
      return null;
    }
  };

  const toggleFilter = (type, value) => {
    let set;
    switch (type) {
      case "category":
        set = selectedCategories;
        break;
      case "subcategory":
        set = selectedSubcategories;
        break;
      case "collection":
        set = selectedCollections;
        break;
      case "commercial":
        set = selectedCommercialNames;
        break;
      case "status":
        set = selectedStatuses;
        break;
      case "vitrine":
        set = selectedVitrines;
        break;
      default:
        return;
    }

    const newSet = new Set(set);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }

    switch (type) {
      case "category":
        setSelectedCategories(newSet);
        break;
      case "subcategory":
        setSelectedSubcategories(newSet);
        break;
      case "collection":
        setSelectedCollections(newSet);
        break;
      case "commercial":
        setSelectedCommercialNames(newSet);
        break;
      case "status":
        setSelectedStatuses(newSet);
        break;
      case "vitrine":
        setSelectedVitrines(newSet);
        break;
    }
  };

  const openGridModal = (familyKey) => {
    setActiveFamilyKey(familyKey);
    setTempGridState({ ...orderState });
    setSelectedBulkColors(new Set());
    setSelectedBulkProducts(new Set());
    setGridModalOpen(true);
  };

  const closeGridModal = () => {
    setGridModalOpen(false);
    setActiveFamilyKey(null);
    setTempGridState({});
  };

  const saveGridSelection = () => {
    setOrderState({ ...tempGridState });
    closeGridModal();
  };


  const toggleBulkSelection = (type, value) => {
    if (type === "color") {
      const newSet = new Set(selectedBulkColors);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      setSelectedBulkColors(newSet);
    } else {
      const newSet = new Set(selectedBulkProducts);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      setSelectedBulkProducts(newSet);
    }
  };

  const clearOrder = () => {
    setConfirmData({
      title: "Limpar Pedido",
      message: "Tem certeza que deseja remover todos os itens do pedido?",
      callback: () => {
        setOrderState({});
        setConfirmModalOpen(false);
      },
    });
    setConfirmModalOpen(true);
  };

  const removeFromCart = (key) => {
    setConfirmData({
      title: "Remover Item",
      message: "Deseja remover este item do pedido?",
      callback: () => {
        const newOrderState = { ...orderState };
        delete newOrderState[key];
        setOrderState(newOrderState);
        setConfirmModalOpen(false);
      },
    });
    setConfirmModalOpen(true);
  };

  const [alertTitle, setAlertTitle] = useState("Pedido Enviado");
  const [alertMessage, setAlertMessage] = useState("Seu pedido foi enviado com sucesso!");

  const submitOrder = async () => {
    const orderStateKeys = Object.keys(orderState);
    const itensPorVitrine = {};

    Object.entries(effectiveFamilies).forEach(([, brandFamilies]) => {
      const familiesObj = typeof brandFamilies === "object" && brandFamilies !== null ? brandFamilies : {};
      Object.entries(familiesObj).forEach(([famKey, famObj]) => {
        if (!famObj || !Array.isArray(famObj.products)) return;
        famObj.products.forEach((prod) => {
          const idVitrineVirtualB2bCliente = prod.idVitrineVirtualB2bCliente;
          if (idVitrineVirtualB2bCliente == null) return;
          const vitrineKey = prod.vitrine;
          const prefix = `${famKey}_${prod.id}_`;
          let totalQty = 0;
          orderStateKeys.forEach((key) => {
            if (key.startsWith(prefix)) {
              const qty = orderState[key];
              if (qty != null && Number(qty) > 0) totalQty += Number(qty);
            }
          });
          if (totalQty <= 0) return;
          const valorUnitario = prod.discount > 0
            ? prod.price * (1 - prod.discount / 100)
            : prod.price;
          if (!itensPorVitrine[vitrineKey]) {
            itensPorVitrine[vitrineKey] = { idVitrineVirtualB2bCliente: idVitrineVirtualB2bCliente, itens: [] };
          }
          itensPorVitrine[vitrineKey].itens.push({
            idProduto: prod.idProduto ?? prod.ref,
            quantidade: totalQty,
            valorUnitario: Math.round(valorUnitario * 100) / 100,
          });
        });
      });
    });

    const vitrinesComItens = Object.entries(itensPorVitrine).filter(([, data]) => data.itens.length > 0);
    if (vitrinesComItens.length === 0) {
      setAlertType("error");
      setAlertTitle("Erro");
      setAlertMessage("Adicione itens ao pedido antes de finalizar.");
      setAlertModalOpen(true);
      return;
    }

    setSubmitOrderLoading(true);
    const resultados = [];
    let erroMsg = null;
    try {
      for (const [, data] of vitrinesComItens) {
        const response = await Api.post(
          "finalize-virtual-showcase-b2b-pedido",
          "showcaseB2B",
          {
            idVitrineVirtualB2bCliente: data.idVitrineVirtualB2bCliente,
            itens: data.itens,
          },
          true
        );
        if (response && response.Success && response.Result?.IdVitrineVirtualB2bPedido) {
          resultados.push(response.Result.IdVitrineVirtualB2bPedido);
        } else {
          erroMsg = response?.ErrorMessage || response?.Message || "Erro ao finalizar pedido.";
          break;
        }
      }

      if (erroMsg) {
        setAlertType("error");
        setAlertTitle("Erro");
        setAlertMessage(erroMsg);
        setAlertModalOpen(true);
        return;
      }
      setOrderState({});
      setCartModalOpen(false);
      setAlertType("success");
      setAlertTitle("Pedido(s) Enviado(s)!");
      setAlertMessage(
        resultados.length > 1
          ? `Seus pedidos foram enviados com sucesso! Pedidos: #${resultados.join(", #")}`
          : `Seu pedido foi enviado com sucesso! Pedido: #${resultados[0]}`
      );
      setAlertModalOpen(true);
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message || "Erro ao finalizar pedido. Tente novamente.";
      setAlertType("error");
      setAlertTitle("Erro");
      setAlertMessage(msg);
      setAlertModalOpen(true);
    } finally {
      setSubmitOrderLoading(false);
    }
  };

  const filteredFamilies = useMemo(() => {
    const brandFamilies = effectiveFamilies[currentBrand];
    return Object.keys(brandFamilies || {}).filter((key) => {
      const fam = brandFamilies[key];
      const catMatch =
        selectedCategories.size === 0 || selectedCategories.has(fam.category);
      const subMatch =
        selectedSubcategories.size === 0 ||
        selectedSubcategories.has(fam.subcategory);
      const colMatch =
        selectedCollections.size === 0 || selectedCollections.has(key);
      const commMatch =
        selectedCommercialNames.size === 0 ||
        selectedCommercialNames.has(fam.commercialName);
      const vitrineMatch =
        selectedVitrines.size === 0 || (fam.products && fam.products.some((p) => selectedVitrines.has(p.vitrine)));
      let statusMatch = true;
      if (selectedStatuses.size > 0) {
        statusMatch = selectedStatuses.has(fam.status);
      }
      return catMatch && subMatch && colMatch && commMatch && statusMatch && vitrineMatch;
    });
  }, [
    currentBrand,
    effectiveFamilies,
    selectedCategories,
    selectedSubcategories,
    selectedCollections,
    selectedCommercialNames,
    selectedStatuses,
    selectedVitrines,
  ]);

  const totals = useMemo(() => {
    let qty = 0;
    let val = 0;
    const brandFamilies = effectiveFamilies[currentBrand];

    Object.entries(orderState).forEach(([key, quantity]) => {
      for (const fam of Object.values(brandFamilies)) {
        for (const prod of fam.products) {
          if (key.includes(prod.id)) {
            let price = prod.price;
            if (prod.discount > 0) {
              price = prod.price * (1 - prod.discount / 100);
            }
            qty += quantity;
            val += quantity * price;
          }
        }
      }
    });

    return { qty, val };
  }, [orderState, currentBrand, effectiveFamilies]);

  useEffect(() => {
    if (effectiveBrandKeys.length === 0) return;
    saveCartForBrand(currentBrand, orderState, effectiveBrandKeys);
  }, [currentBrand, orderState, effectiveBrandKeys]);

  const handleRequestShowIntro = () => {
    if (Object.keys(orderState).length > 0) {
      setConfirmData({
        title: "Trocar de marca",
        message: "Deseja trocar de marca? O carrinho atual será limpo.",
        callback: () => {
          saveCartForBrand(currentBrand, {}, effectiveBrandKeys);
          setOrderState({});
          setShowIntroScreen(true);
          setConfirmModalOpen(false);
        },
      });
      setConfirmModalOpen(true);
    } else {
      setShowIntroScreen(true);
    }
  };

  const handleSelectBrand = (name) => {
    saveCartForBrand(currentBrand, orderState, effectiveBrandKeys);
    setCurrentBrand(name);
    setOrderState(getCartForBrand(name, effectiveBrandKeys));
    setShowIntroScreen(false);
  };

  if (loading) {
    return (
      <Box
        className="fazer-pedido-page vitrine-empresa-loading"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress size={48} />
        <Typography style={{ marginTop: 16 }}>Carregando vitrine...</Typography>
      </Box>
    );
  }

  return (
    <Box className={`theme-${currentBrand === "karsten_decor" ? "karsten" : currentBrand} fazer-pedido-page`}>
      {showIntroScreen && (
        <IntroBrandScreen
          onSelectBrand={handleSelectBrand}
          brandKeysWithProducts={brandKeysWithProducts}
        />
      )}
      {!showIntroScreen && primaryVitrine && (
        <>
          <Header
            selectedBrand={currentBrand}
            theme={theme}
            onShowIntroScreen={handleRequestShowIntro}
            currentBrandLabel={effectiveBrandLabels[currentBrand] || currentBrand}
          />
          <Box className="main-container">
            <Sidebar
              currentBrand={currentBrand}
              families={effectiveFamilies}
              selectedCategories={selectedCategories}
              selectedSubcategories={selectedSubcategories}
              selectedCollections={selectedCollections}
              selectedCommercialNames={selectedCommercialNames}
              selectedStatuses={selectedStatuses}
              selectedVitrines={selectedVitrines}
              categorySearchTerm={categorySearchTerm}
              setCategorySearchTerm={setCategorySearchTerm}
              subcategorySearchTerm={subcategorySearchTerm}
              setSubcategorySearchTerm={setSubcategorySearchTerm}
              commercialSearchTerm={commercialSearchTerm}
              setCommercialSearchTerm={setCommercialSearchTerm}
              collectionSearchTerm={collectionSearchTerm}
              setCollectionSearchTerm={setCollectionSearchTerm}
              productSearchTerm={productSearchTerm}
              setProductSearchTerm={setProductSearchTerm}
              onlyStock={onlyStock}
              setOnlyStock={setOnlyStock}
              toggleFilter={toggleFilter}
              theme={theme}
              vitrinesMeta={vitrinesMeta}
            />
            <Box className="main-content">
              <ProductList
                currentBrand={currentBrand}
                families={effectiveFamilies}
                filteredFamilies={filteredFamilies}
                orderState={orderState}
                setOrderState={setOrderState}
                openGridModal={openGridModal}
                productSearchTerm={productSearchTerm}
                theme={theme}
              />
            </Box>
          </Box>
          <Footer
            totals={totals}
            clearOrder={clearOrder}
            openCartModal={() => setCartModalOpen(true)}
            openOrdersModal={() => setOrdersModalOpen(true)}
            theme={theme}
          />
          <GridModal
            open={gridModalOpen}
            onClose={closeGridModal}
            onSave={saveGridSelection}
            currentBrand={currentBrand}
            activeFamilyKey={activeFamilyKey}
            families={effectiveFamilies}
            tempGridState={tempGridState}
            setTempGridState={setTempGridState}
            selectedBulkColors={selectedBulkColors}
            selectedBulkProducts={selectedBulkProducts}
            toggleBulkSelection={toggleBulkSelection}
            theme={theme}
          />
          <CartModal
            open={cartModalOpen}
            onClose={() => setCartModalOpen(false)}
            currentBrand={currentBrand}
            families={effectiveFamilies}
            orderState={orderState}
            isCartGrouped={isCartGrouped}
            toggleCartGrouping={() => setIsCartGrouped(!isCartGrouped)}
            removeFromCart={removeFromCart}
            submitOrder={submitOrder}
            submitOrderLoading={submitOrderLoading}
            businessRules={effectiveBusinessRules}
            theme={theme}
            vitrinesMeta={vitrinesMeta}
          />
          <OrdersModal
            open={ordersModalOpen}
            onClose={() => setOrdersModalOpen(false)}
            pedidos={pedidosList}
            loading={loadingPedidos}
            fetchPedidoDetalhe={fetchPedidoDetalhe}
            theme={theme}
          />
          <ConfirmModal
            open={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={() => {
              if (confirmData.callback) confirmData.callback();
            }}
            title={confirmData.title}
            message={confirmData.message}
            theme={theme}
          />
        </>
      )}
      <AlertModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        title={alertTitle}
        message={alertMessage}
        theme={theme}
        type={alertType}
      />
    </Box>
  );
}
